import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@monbits/ui";
import { Edit2, MoreVertical, Trash2 } from "lucide-react";
import * as React from "react";
import AddressCreateForm from "src/components/address-create-form/address-create-form";
import AddressEditForm from "src/components/address-edit-form/address-edit-form";
import { ConfirmationDialog } from "src/components/confirmation-dialog";
import DrawerForm from "src/components/drawer-form/drawer-form";
import { Loader } from "src/components/loader/loader";
import addressQueries from "src/queries/address-queries";
import type { MerchantAddress, MerchantAddressParams } from "src/types";
import { sleep } from "src/utils";

interface Props {
	openPopupFunc: (title: string, desc: string) => void;
}

const WithdrawAddresses: React.FC<Props> = (props: Props) => {
	const listAddresses = addressQueries.listAddresses();
	const createAddress = addressQueries.createAddress();
	const updateAddress = addressQueries.updateAddress();
	const deleteAddress = addressQueries.deleteAddress();
	const [addresses, setAddresses] = React.useState<MerchantAddress[]>(
		listAddresses.data || [],
	);
	const [isAddAddressFormOpen, setIsAddAddressFormOpen] =
		React.useState<boolean>(false);
	const [openedAddress, changeOpenedAddress] = React.useState<
		MerchantAddress[]
	>([]);
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
		React.useState<boolean>(false);
	const [addressToDelete, setAddressToDelete] =
		React.useState<MerchantAddress | null>(null);

	const isLoading =
		listAddresses.isLoading ||
		listAddresses.isFetching ||
		createAddress.isPending ||
		updateAddress.isPending ||
		deleteAddress.isPending;

	const deleteSelectedAddress = async (id: string, name: string) => {
		try {
			await deleteAddress.mutateAsync(id);
			changeOpenedAddress([]);
			setIsDeleteDialogOpen(false);
			setAddressToDelete(null);
			props.openPopupFunc(
				"Address has deleted",
				`Address ${name} has been deleted`,
			);
		} catch (error) {
			console.error("error occurred: ", error);
		}
	};

	React.useEffect(() => {
		setAddresses(listAddresses.data || []);
	}, [listAddresses.data]);

	React.useEffect(() => {
		listAddresses.refetch();
	}, [listAddresses.refetch]);

	const uploadCreatedAddress = async (
		value: MerchantAddressParams,
		reset: () => void,
	) => {
		try {
			setIsFormSubmitting(true);
			await createAddress.mutateAsync(value);
			setIsAddAddressFormOpen(false);
			props.openPopupFunc(
				"Address has created",
				`You have created new address ${value.name}`,
			);

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
			throw error;
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const uploadNewAddress = async (
		value: MerchantAddress,
		reset: () => void,
	) => {
		try {
			setIsFormSubmitting(true);
			await updateAddress.mutateAsync(value);
			changeOpenedAddress([]);
			props.openPopupFunc(
				"Address has edited",
				`You have edited your address ${value.name}`,
			);

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const _setOpenedAddress = (value: boolean) => {
		if (!value) {
			changeOpenedAddress([]);
		}
	};

	return (
		<>
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-2xl font-semibold">Withdrawal Addresses</h3>
				<Button onClick={() => setIsAddAddressFormOpen(true)}>
					Add address
				</Button>
			</div>

			{isLoading ? (
				<Loader size="md" fullScreen />
			) : !addresses || addresses.length === 0 ? (
				<div className="flex flex-col items-center gap-4 py-16 px-8 text-center">
					<div className="space-y-2 max-w-md">
						<h3 className="text-base font-medium">
							No withdrawal addresses yet
						</h3>
						<p className="text-sm text-muted-foreground">
							Save addresses you withdraw to often, so you don't have to paste
							them every time.
						</p>
					</div>
					<Button onClick={() => setIsAddAddressFormOpen(true)}>
						Add your first address
					</Button>
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[15%]">Network</TableHead>
							<TableHead className="w-[20%]">Name</TableHead>
							<TableHead>Address</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{addresses.map((record) => (
							<TableRow key={record.id}>
								<TableCell className="font-medium whitespace-nowrap">
									{record.blockchainName}
								</TableCell>
								<TableCell className="font-medium whitespace-nowrap">
									{record.name}
								</TableCell>
								<TableCell>
									<div className="flex items-center justify-between">
										<span className="font-mono text-sm">{record.address}</span>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													aria-label="Address actions"
												>
													<MoreVertical className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem
													className="cursor-pointer"
													onClick={() => changeOpenedAddress([record])}
												>
													<Edit2 className="mr-2 h-4 w-4" />
													Edit
												</DropdownMenuItem>
												<DropdownMenuItem
													className="text-destructive focus:text-destructive cursor-pointer"
													onClick={() => {
														setAddressToDelete(record);
														setIsDeleteDialogOpen(true);
													}}
												>
													<Trash2 className="mr-2 h-4 w-4" />
													Delete
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<DrawerForm
				title="Create an address"
				description="Add a new withdrawal address for your crypto transactions."
				isOpen={isAddAddressFormOpen}
				onClose={() => setIsAddAddressFormOpen(false)}
				onSubmit={() => {
					const form = document.querySelector("form");
					if (form) {
						form.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={isFormSubmitting}
				submitText="Save Address"
				width={512}
			>
				<AddressCreateForm
					onFinish={uploadCreatedAddress}
					isFormSubmitting={isFormSubmitting}
				/>
			</DrawerForm>
			<DrawerForm
				title="Edit withdrawal address"
				description="Update the name for this withdrawal address."
				isOpen={Boolean(openedAddress.length)}
				onClose={() => changeOpenedAddress([])}
				onSubmit={() => {
					const form = document.querySelector("form");
					if (form) {
						form.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={isFormSubmitting}
				submitText="Save Changes"
				width={512}
			>
				{openedAddress[0] && (
					<AddressEditForm
						onCancel={() => {
							changeOpenedAddress([]);
						}}
						onFinish={uploadNewAddress}
						selectedAddress={openedAddress[0]}
						isFormSubmitting={isFormSubmitting}
					/>
				)}
			</DrawerForm>

			<ConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				onConfirm={() => {
					if (addressToDelete) {
						return deleteSelectedAddress(
							addressToDelete.id,
							addressToDelete.name,
						);
					}
				}}
				title="Delete withdrawal address"
				description="Are you sure you want to delete this withdrawal address? This action cannot be undone."
				confirmText="Delete"
				variant="destructive"
			/>
		</>
	);
};

export default WithdrawAddresses;
